import { useMemo, useState } from "react";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { resolveReceiveSearchAdd } from "@/lib/manufacturing/receiveLineAdd";
import { shouldOfferReceiveUnitToggle } from "@/lib/manufacturing/receiveQtyConversion";
import type { useManufacturingReceiveLines } from "./useManufacturingReceiveLines";
import { inputClass, mfgGhostBtnSemiboldClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

const SEARCH_MATCH_LIMIT = 12;

type Props = {
  receive: ReturnType<typeof useManufacturingReceiveLines>;
  materials: readonly MfgMaterial[];
};

export function ManufacturingReceiveLinesBody({ receive, materials }: Props) {
  const activeMaterials = useMemo(() => materials.filter((m) => m.is_active), [materials]);

  const [materialSearch, setMaterialSearch] = useState("");
  const [needsChoice, setNeedsChoice] = useState(false);
  const filteredMaterials = useMemo(() => {
    const q = materialSearch.trim().toLowerCase();
    if (!q) return activeMaterials;
    return activeMaterials.filter((m: MfgMaterial) =>
      [m.name, m.code, m.category].some((field) => (field || "").toLowerCase().includes(q))
    );
  }, [activeMaterials, materialSearch]);
  const searchMatches = useMemo(
    () => filteredMaterials.map((m) => ({ id: m.id, name: m.name, code: m.code })),
    [filteredMaterials],
  );
  const searchDecision = useMemo(
    () => resolveReceiveSearchAdd(materialSearch, searchMatches),
    [materialSearch, searchMatches],
  );
  const query = materialSearch.trim();
  const visibleMatches = query ? filteredMaterials.slice(0, SEARCH_MATCH_LIMIT) : [];
  const hiddenMatchCount = query ? Math.max(0, filteredMaterials.length - visibleMatches.length) : 0;
  const addMatch =
    searchDecision.action === "add"
      ? filteredMaterials.find((m) => m.id === searchDecision.materialId)
      : undefined;

  function commitSearchedMaterial(materialId?: string) {
    if (typeof materialId === "string" && materialId) {
      receive.addLine(materialId);
      setMaterialSearch("");
      setNeedsChoice(false);
      return;
    }
    if (searchDecision.action === "blank") {
      receive.addLine();
      setNeedsChoice(false);
      return;
    }
    if (searchDecision.action === "add") {
      receive.addLine(searchDecision.materialId);
      setMaterialSearch("");
      setNeedsChoice(false);
      return;
    }
    setNeedsChoice(true);
  }

  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "mfg-receive-lines" }} className="space-y-3">
      <div className="flex flex-wrap items-stretch gap-2">
        <input
          type="search"
          className={`${inputClass} flex-1 min-w-0`}
          value={materialSearch}
          onChange={(e) => {
            setMaterialSearch(e.target.value);
            setNeedsChoice(false);
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            commitSearchedMaterial();
          }}
          placeholder={`Search materials (name, code, category) — ${activeMaterials.length} active`}
          aria-label="Search for a material to add to this receipt"
        />
        <button
          type="button"
          className={mfgGhostBtnSemiboldClass}
          onClick={() => commitSearchedMaterial()}
        >
          {addMatch ? `+ Add ${addMatch.code.trim() || addMatch.name}` : "+ Add Line"}
        </button>
      </div>
      <p className="text-[11px] text-gray-500 dark:text-zinc-400">
        Search for a material, then Add Line. One match is added to the receipt. If several match, pick one from the list.
      </p>
      {query && filteredMaterials.length === 0 ? (
        <div className="text-xs text-amber-700 dark:text-amber-300">
          No active materials match &ldquo;{materialSearch}&rdquo;. Clear the search to see every material again.
        </div>
      ) : null}
      {needsChoice && searchDecision.action === "choose" ? (
        <div className="text-xs text-amber-700 dark:text-amber-300">
          Several materials match &ldquo;{materialSearch}&rdquo;. Pick one below to add it.
        </div>
      ) : null}
      {visibleMatches.length > 0 ? (
        <ul className="max-h-48 overflow-auto rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
          {visibleMatches.map((m) => {
            const isAddTarget = addMatch?.id === m.id;
            return (
              <li key={m.id} className="border-b border-gray-100 last:border-b-0 dark:border-zinc-800">
                <button
                  type="button"
                  className={
                    "block w-full px-3 py-2 text-left text-sm hover:bg-emerald-50 dark:hover:bg-emerald-950/40 " +
                    (isAddTarget ? "bg-emerald-50 dark:bg-emerald-950/30" : "")
                  }
                  onClick={() => commitSearchedMaterial(m.id)}
                >
                  <span className="font-medium text-gray-900 dark:text-zinc-100">{m.name}</span>
                  <span className="text-gray-500 dark:text-zinc-400"> ({m.code})</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {hiddenMatchCount > 0 ? (
        <p className="text-[11px] text-gray-500 dark:text-zinc-400">
          {hiddenMatchCount} more match{hiddenMatchCount === 1 ? "" : "es"}. Narrow the search to see them.
        </p>
      ) : null}
      {receive.lines.map((line, idx) => {
        const selected = activeMaterials.find((m) => m.id === line.materialId);
        const showToggle = selected ? shouldOfferReceiveUnitToggle(selected) : false;
        const shipLabel = selected?.shipping_unit?.trim() || "shipping unit";
        const unitLabel = selected?.unit?.trim() || "unit";
        const resolved = receive.resolveLineFactor(line);
        const factor = resolved.factor;
        const aliases = receive.aliasesByMaterial[line.materialId] ?? [];

        return (
          <div
            key={line.id}
            className="space-y-2 rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-zinc-700 dark:bg-zinc-800/50"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600 dark:text-zinc-300">Line {idx + 1}</span>
              {receive.lines.length > 1 ? (
                <button
                  type="button"
                  className="rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[11px] text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
                  onClick={() => receive.removeLine(line.id)}
                >
                  Remove
                </button>
              ) : null}
            </div>
            <select
              className={inputClass}
              value={line.materialId}
              onChange={(e) => receive.updateLine(line.id, { materialId: e.target.value })}
            >
              <option value="">— Select material —</option>
              {filteredMaterials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.code})
                </option>
              ))}
              {line.materialId && !filteredMaterials.some((m) => m.id === line.materialId)
                ? (() => {
                    const current = activeMaterials.find((m) => m.id === line.materialId);
                    return current ? (
                      <option key={`current-${current.id}`} value={current.id}>
                        {current.name} ({current.code}) — current selection
                      </option>
                    ) : null;
                  })()
                : null}
            </select>
            {line.materialId && aliases.length > 0 ? (
              <label className="block text-xs text-gray-600 dark:text-zinc-300">
                Vendor alias (optional)
                <select
                  className={`${inputClass} mt-1`}
                  value={line.aliasId}
                  onChange={(e) => receive.updateLine(line.id, { aliasId: e.target.value })}
                >
                  <option value="">Catalog default</option>
                  {aliases.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.vendor_name}
                      {a.vendor_sku ? ` · ${a.vendor_sku}` : ""}
                      {Number(a.unit_conversion_factor) > 0
                        ? ` (×${Number(a.unit_conversion_factor)})`
                        : ""}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {showToggle ? (
              <div className="flex flex-wrap gap-2" role="group" aria-label="Receive quantity unit">
                <button
                  type="button"
                  className={
                    line.qtyMode === "shipping"
                      ? "rounded-md border border-emerald-600 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-800 dark:border-emerald-500 dark:bg-emerald-950/40 dark:text-emerald-200"
                      : "rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                  }
                  onClick={() => receive.updateLine(line.id, { qtyMode: "shipping" })}
                >
                  Receiving in {shipLabel}
                </button>
                <button
                  type="button"
                  className={
                    line.qtyMode === "canonical"
                      ? "rounded-md border border-emerald-600 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-800 dark:border-emerald-500 dark:bg-emerald-950/40 dark:text-emerald-200"
                      : "rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                  }
                  onClick={() => receive.updateLine(line.id, { qtyMode: "canonical" })}
                >
                  Receiving in {unitLabel}
                </button>
                {line.qtyMode === "shipping" ? (
                  <span className="self-center text-[11px] text-gray-500 dark:text-zinc-400">
                    × {factor} {unitLabel}/{shipLabel} · {resolved.sourceLabel}
                  </span>
                ) : null}
              </div>
            ) : null}
            {line.materialId && !showToggle ? (
              <div className="text-[11px] text-gray-500 dark:text-zinc-400">
                Conversion: {resolved.sourceLabel}
                {factor !== 1 ? ` (×${factor})` : ""}
              </div>
            ) : null}
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs text-gray-600 dark:text-zinc-300">
                Qty received
                {showToggle ? ` (${line.qtyMode === "shipping" ? shipLabel : unitLabel})` : ""}
                <input
                  className={`${inputClass} mt-1`}
                  type="number"
                  min="0"
                  step="any"
                  placeholder="e.g. 100"
                  value={line.qty}
                  onChange={(e) => receive.updateLine(line.id, { qty: e.target.value })}
                />
              </label>
              <label className="block text-xs text-gray-600 dark:text-zinc-300">
                Unit cost ($/{unitLabel})
                <input
                  className={`${inputClass} mt-1`}
                  type="number"
                  min="0"
                  step="any"
                  placeholder="e.g. 4.25"
                  value={line.unitCost}
                  onChange={(e) => receive.updateLine(line.id, { unitCost: e.target.value })}
                />
              </label>
            </div>
            <input
              className={inputClass}
              placeholder="Notes (optional)"
              value={line.notes}
              onChange={(e) => receive.updateLine(line.id, { notes: e.target.value })}
            />
          </div>
        );
      })}

      <button
        type="button"
        {...{ [FIREFLY_TARGET_ATTR]: "mfg-receive-post" }}
        className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
        disabled={receive.working}
        onClick={() => void receive.submit()}
      >
        {receive.working ? "Posting…" : "Post receipt"}
      </button>
    </div>
  );
}
